import { DateTime } from 'luxon';
import { useMemo } from 'react';

import EmptyContainer from 'components/app-components/home/EmptyContainer';
import { GenericTable } from 'components/generic-components/generic-table/GenericTable';
import TransactionsIcon from 'icons/Transactions';
import { Column } from 'components/generic-components/generic-table/type';
import StatusPill, { IPillStatus } from 'components/app-components/ui/status-pill';
import { removeUnderscoreAndCapitalizeFirstLetters } from 'utils/string';
import { useUser } from 'context/user';
import useIntl from 'hooks/useIntl';
import { useBridgeWalletTransactionsHistory } from 'hooks/useBridgeSWR';
import { useGetWorkerTransactions } from 'hooks/useWisePayments';
import { useTheraAdvanceHistorySWR, useTheraAdvanceSWR } from 'hooks/useTheraAdvanceSWR';
import { TheraAdvanceStatus } from 'lib/constants';
import Wallet from 'icons/Wallet';
import BankTransferIcon from 'icons/BankTransferIcon';
import ArrowBackInvoiceModal from 'icons/ArrowBackInvoiceModal';
import { WalletHistory } from '@thera-hr/api-sdk/api/resources/api/resources/thera/resources/bridge';
import {
  TransactionTypeFilter,
  TRANSACTION_TYPE_LABELS,
  normalizeTheraAdvanceEntries,
  normalizeWorkerTransactions,
  normalizeWalletTransactions,
  mergeAndSortTransactions,
  filterUnifiedByType,
} from 'utils/unifiedTransactions';
import { useUnifiedTabState } from 'hooks/useUnifiedTabState';
import { useUnifiedColumns } from 'utils/unifiedTransactionColumns';
import Dropdown from 'components/generic-components/dropdown/Dropdown';

export const getAccountTypeIcon = (type: 'credit' | 'debit') => {
  switch (type) {
    case 'credit':
      return <Wallet />;
    case 'debit':
      return <BankTransferIcon />;
    default:
      return <BankTransferIcon />;
  }
};

const formatDateTime = (timestamp: Date) => {
  const dt = DateTime.fromJSDate(timestamp, { zone: 'local' });
  return {
    date: dt.toFormat('d LLL, yyyy'),
    time: dt.toFormat('hh:mm a'),
  };
};

const WalletWithdrawalHistory = () => {
  const { user, currentContract } = useUser();
  const walletId = user?.bridgeUserDetails?.wallet?.id;
  const theraAdvanceId = user?.employeeInformation?.settingsByCompanyId?.[currentContract?.companyID]?.theraAdvanceId;
  const { formatCurrencyToLocale } = useIntl();
  // Fetch virtual accounts history
  const { walletTransactions, isPending } = useBridgeWalletTransactionsHistory({ walletId });

  const { theraAdvance } = useTheraAdvanceSWR({ theraAdvanceId });
  const isTheraAdvanceVisible = theraAdvanceId && theraAdvance?.status !== TheraAdvanceStatus.CANCELLED;

  const availableTypes = useMemo<TransactionTypeFilter[]>(
    () => [
      'wallet',
      'withdrawals',
      ...(isTheraAdvanceVisible ? (['thera_advance'] as TransactionTypeFilter[]) : []),
      'all',
    ],
    [isTheraAdvanceVisible],
  );

  const { selectedType, handleTypeChange, handleUnifiedRowClick } = useUnifiedTabState('wallet', availableTypes);

  const isUnifiedView = selectedType !== 'wallet';
  const { transactions: workerTransactions, isPending: isWorkerPending } = useGetWorkerTransactions({
    userId: isUnifiedView ? user?.userID : '',
    companyId: isUnifiedView ? currentContract?.companyID : '',
  });
  const { entries: advanceEntries, isPending: isAdvancePending } = useTheraAdvanceHistorySWR({
    theraAdvanceId: isUnifiedView ? theraAdvanceId : undefined,
  });

  const allUnified = useMemo(
    () =>
      mergeAndSortTransactions(
        normalizeTheraAdvanceEntries(advanceEntries || []),
        normalizeWorkerTransactions(workerTransactions || []),
        normalizeWalletTransactions(walletTransactions || []),
      ),
    [advanceEntries, workerTransactions, walletTransactions],
  );

  const filteredUnified = useMemo(() => filterUnifiedByType(allUnified, selectedType), [allUnified, selectedType]);
  const isUnifiedPending = isPending || isWorkerPending || isAdvancePending;
  const unifiedColumns = useUnifiedColumns();

  interface WalletTransaction extends WalletHistory {
    timestamp: Date;
    status: string;
    amount: string;
    isDebit: boolean;
  }

  const transactions = useMemo(() => {
    return (
      walletTransactions?.map((tx) => {
        // Determine status based on transaction type
        const status = 'completed';
        const amount = Math.round(Number(tx.amount) * 100);
        const isDebit = tx.source.paymentRail === 'bridge_wallet';

        return {
          ...tx,
          timestamp: new Date(tx.createdAt),
          status,
          amount: amount.toString(),
          isDebit,
        } as WalletTransaction;
      }) || []
    );
  }, [walletTransactions]);

  const columns: Column<WalletTransaction>[] = [
    {
      header: 'TRANSACTION DETAIL',
      cell: ({ row }) => {
        const { source } = row.original;
        const isDebit = source.paymentRail === 'bridge_wallet';
        const txnDetails = `Wallet ${isDebit ? 'Debit' : 'Credit'} · ${source.currency?.toUpperCase()}`;

        return (
          <div className="flex items-center">
            <div className="relative mr-3">
              <span className="flex h-10 w-10 min-w-10 items-center justify-center rounded-full border border-gray-200 bg-white">
                {getAccountTypeIcon(isDebit ? 'debit' : 'credit')}
              </span>
              <span className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-neutral-100">
                <ArrowBackInvoiceModal
                  className={`h-3 w-3 ${isDebit ? 'rotate-135 transform' : '-rotate-45 transform'}`}
                />
              </span>
            </div>
            <div className="flex flex-col">
              <div className="flex flex-wrap items-center">
                <p className="text-sm font-normal text-neutral-900">{txnDetails}</p>
              </div>
            </div>
          </div>
        );
      },
      id: 'transaction_data',
      accessorKey: 'transaction_data',
      filterFn: 'arrIncludesSome',
      meta: {
        className: '!text-sm !font-normal !text-neutral-900',
      },
      accessorFn: ({ source }) => {
        return `${removeUnderscoreAndCapitalizeFirstLetters(source.paymentRail)} · ${source.currency}`;
      },
    },
    {
      header: 'DATE',
      cell: () => '',
      id: 'timestamp',
      accessorKey: 'timestamp',
      filterFn: 'arrIncludesSome',
      meta: {
        className: '!text-sm !font-normal !text-neutral-900',
        icon: (row) => {
          return (
            <p>
              {formatDateTime(row.timestamp).date}
              <span className="ml-2 text-xs text-neutral-500">{formatDateTime(row.timestamp).time}</span>
            </p>
          );
        },
      },
      accessorFn: (row) => formatDateTime(row.timestamp).date,
    },
    {
      header: 'PAYMENT STATUS',
      cell: () => '',
      accessorKey: 'status',
      filterFn: 'arrIncludesSome',
      id: 'status',
      meta: {
        className: '!text-sm !font-normal !text-neutral-900',
        icon: () => {
          const statusText = 'Completed';

          const statusType: IPillStatus = statusText === 'Completed' ? 'ready' : 'inProgress';
          return <StatusPill text={statusText} status={statusType} />;
        },
      },
      accessorFn: () => {
        return 'Completed';
      },
    },
    {
      header: 'AMOUNT',
      cell: () => '',
      accessorKey: 'amount',
      id: 'amount',
      filterFn: 'arrIncludesSome',
      hideJustFromFilterList: true,
      accessorFn: ({ amount }) => {
        const finalAmount = Number(amount) / 100;
        return `${finalAmount}`;
      },
      meta: {
        className: '!text-sm !font-normal !text-neutral-900',
        icon: ({ amount, source }) => {
          const isCrypto = ['usdc', 'usdt', 'usdb', 'eurc'].includes(source.currency.toLowerCase());
          const cryptoAmount = Number(amount) / 100;
          const isDebit = source.paymentRail === 'bridge_wallet';
          const formattedAmount = isCrypto
            ? `${cryptoAmount} ${source.currency?.toUpperCase()}`
            : formatCurrencyToLocale(Number(amount), source?.currency);
          const displayText = `${isDebit ? '-' : '+'}${formattedAmount}`;
          return (
            <div className="font-medium">
              <p data-sensitive-copy-admins>{displayText}</p>
            </div>
          );
        },
      },
    },
  ];

  const typeSelect = (
    <Dropdown
      id="wallet-type-select"
      values={availableTypes.map((type) => ({ value: type, label: TRANSACTION_TYPE_LABELS[type] }))}
      fieldDisplayName=""
      name="transaction-type"
      selectedItem={selectedType}
      onSelectedItemChange={(val) => val && handleTypeChange(val as TransactionTypeFilter)}
      doNotClearField
      containerClassName="min-w-[160px]"
    />
  );

  return (
    <div className="mt-6 rounded-lg border border-gray-200 p-4 pb-0">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-lg font-medium text-neutral-900">Wallet History</h2>
        {typeSelect}
      </div>

      {(() => {
        if (selectedType === 'wallet') {
          return !isPending && !transactions?.length ? (
            <EmptyContainer
              text="Once you withdraw your balance, it will appear here!"
              containerClass="p-4 md:p-6 mb-6"
              icon={<TransactionsIcon className="h-7 w-7" />}
            />
          ) : (
            <GenericTable<WalletTransaction>
              key="wallet"
              data={transactions}
              columns={columns}
              isPending={isPending}
              showGlobalFilter={true}
              showViewAllBtn={false}
              pageSize={10}
              showPageSizeSelector
              showFilters={true}
              initialState={{ columnVisibility: {} }}
            />
          );
        }

        return !isUnifiedPending && !filteredUnified?.length ? (
          <EmptyContainer
            text="No transactions found."
            containerClass="p-4 md:p-6 mb-6"
            icon={<TransactionsIcon className="h-7 w-7" />}
          />
        ) : (
          <GenericTable
            key={selectedType}
            data={filteredUnified}
            columns={unifiedColumns}
            isPending={isUnifiedPending}
            showGlobalFilter={true}
            showViewAllBtn={false}
            pageSize={10}
            showPageSizeSelector
            showFilters={true}
            compact
            onRowClick={handleUnifiedRowClick}
          />
        );
      })()}
    </div>
  );
};

export default WalletWithdrawalHistory;
